import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Lock, Plus, Unlock } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { api, errMsg, imgSrc, money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

function SellDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ title: "", description: "", price: "9.99" });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    const fd = new FormData();
    Object.entries(f).forEach(([k, v]) => fd.append(k, v));
    fd.append("file", file);
    setBusy(true);
    try { await api.post("/content", fd); toast.success("Your content is live"); setOpen(false); onCreated(); }
    catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  const net = (Number(f.price || 0) * 0.9).toFixed(2);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><button data-testid="sell-content-btn" className="btn-red flex items-center gap-2 rounded-full px-6 py-3 text-sm font-semibold"><Plus className="h-4 w-4" />Sell content</button></DialogTrigger>
      <DialogContent data-testid="sell-dialog" className="border-zinc-800 bg-zinc-950">
        <DialogHeader><DialogTitle className="font-display text-3xl">List exclusive content</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <Input data-testid="sell-title-input" required placeholder="Title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className="bg-zinc-900" />
          <Textarea data-testid="sell-description-input" placeholder="Description" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} className="bg-zinc-900" />
          <Input data-testid="sell-price-input" type="number" step="0.01" min="1" required value={f.price} onChange={(e) => setF({ ...f, price: e.target.value })} className="bg-zinc-900" />
          <input data-testid="sell-file-input" type="file" accept="image/*" required onChange={(e) => setFile(e.target.files[0])} className="text-sm text-zinc-400" />
          <p className="text-xs text-zinc-500">You earn ${net} per sale · Diamond Luxury keeps 10%. VIP, 18+ and ID verified only.</p>
          <button disabled={busy} data-testid="sell-submit-btn" className="btn-red w-full rounded-full py-3 font-semibold disabled:opacity-50">{busy ? "Uploading…" : "Publish"}</button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ItemCard({ it, onBuy }) {
  return (
    <div data-testid={`content-item-${it.id}`} className="card-lux overflow-hidden">
      <div className="relative aspect-[4/5] overflow-hidden bg-zinc-900">
        {it.unlocked ? <img src={imgSrc(it.media)} alt={it.title} className="h-full w-full object-cover" /> : (
          <>
            <img src={imgSrc(it.creator?.photo)} alt="" className="h-full w-full scale-110 object-cover opacity-40 blur-2xl" />
            <div className="absolute inset-0 flex flex-col items-center justify-center"><Lock className="h-8 w-8 text-red-500" /><p className="eyebrow mt-3">Exclusive · 18+</p></div>
          </>
        )}
      </div>
      <div className="p-5">
        <Link to={`/members/${it.creator?.id}`} className="eyebrow !text-red-400 hover:!text-white">{it.creator?.name}</Link>
        <h3 className="font-display mt-1 text-2xl">{it.title}</h3>
        <p className="mt-1 line-clamp-2 text-sm text-zinc-500">{it.description}</p>
        <div className="mt-5 flex items-center justify-between">
          <span className="font-display text-3xl">{money(it.price_cents)}</span>
          {it.is_mine ? <span className="text-xs text-zinc-400">{it.sales_count} sales</span> : it.unlocked ? (
            <span className="flex items-center gap-1 text-xs text-emerald-400"><Unlock className="h-3 w-3" />Unlocked</span>
          ) : (
            <button data-testid={`content-buy-btn-${it.id}`} onClick={() => onBuy(it)} className="btn-red rounded-full px-5 py-2 text-sm font-semibold">Unlock</button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Market() {
  const { user, refresh } = useAuth();
  const [items, setItems] = useState([]);
  const load = () => api.get("/content").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const buy = async (it) => {
    try { await api.post(`/content/${it.id}/buy`); toast.success(`Unlocked “${it.title}”`); load(); refresh(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <main className="mx-auto max-w-7xl px-5 py-12 lg:px-8" data-testid="market-page">
      <div className="mb-10 flex flex-wrap items-end justify-between gap-6">
        <div>
          <p className="eyebrow mb-3">18+ · VIP creators</p>
          <h1 className="font-display text-5xl sm:text-6xl">Exclusive <span className="italic text-red-600">collection</span></h1>
          <p className="mt-3 text-sm text-zinc-400">Paid from your wallet balance ({money(user.wallet_cents)}). <Link to="/wallet" className="text-red-500">Add funds</Link></p>
        </div>
        <SellDialog onCreated={load} />
      </div>
      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {items.map((it) => <ItemCard key={it.id} it={it} onBuy={buy} />)}
      </div>
    </main>
  );
}
